"use strict";

/**
 * UserSelectAutocomplete renders a user search field backed by a remote API,
 * exposing a debounced autocomplete that keeps existing assignees visible and
 * avoids redundant empty searches when opened while the user is typing.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import axios from "axios";
import { debounce } from "lodash";
import "./styles.css";

/**
 * @param {Object} props
 * @param {(user: {id: string, name: string, email: string, isAdmin: boolean}) => void} props.onUserSelect
 * @param {Array} props.existingUsers
 * @param {string} props.fetchUsersUrl
 * @param {boolean} props.applyWidth
 * @param {{id: string, name: string, email?: string, isAdmin?: boolean} | null} props.selectedUser
 * @param {boolean} props.disabled
 */
const UserSelectAutocomplete = ({
    onUserSelect,
    existingUsers = [],
    fetchUsersUrl,
    applyWidth = true,
    selectedUser: selectedUserProp,
    disabled = false,
}) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [hasFetchedEmpty, setHasFetchedEmpty] = useState(false);
    const latestSearchTermRef = useRef("");
    const isControlled = selectedUserProp !== undefined;

    /**
     * Fetch users that match the given search term while excluding existing selections.
     * @param {string} searchTerm
     */
    const fetchUsersAPI = useCallback(async (searchTerm) => {
        const normalizedSearch = searchTerm || "";
        setLoading(true);

        try {
            // Request matching users from the server.
            const response = await axios.post(
                fetchUsersUrl,
                {
                    search: normalizedSearch,
                    limit: 5,
                }
            );

            // Keep already-assigned users in the results but tag them so the UI
            // can hint that they are part of the project already.
            if (latestSearchTermRef.current !== normalizedSearch) {
                return;
            }
            if (response.data?.users) {
                const annotatedUsers = response.data.users.map((user) => ({
                    ...user,
                    alreadyAssigned: existingUsers.some((existing) => existing.id === user.id),
                }));
                setOptions(annotatedUsers);
            } else {
                setOptions([]);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            setOptions([]);
        } finally {
            if (latestSearchTermRef.current === normalizedSearch) {
                setLoading(false);
            }
        }
    }, [existingUsers, fetchUsersUrl]);

    /**
     * Debounced search handler to limit network calls while typing.
     */
    const debouncedSearch = useMemo(
        () => debounce((term) => {
            if (term.length >= 2 || term.length === 0) {
                fetchUsersAPI(term);
            }
        }, 500),
        [fetchUsersAPI]
    );

    // Cleanup the debounced handler on unmount.
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    useEffect(() => {
        if (isControlled) {
            setSelectedUser(selectedUserProp);
        }
    }, [isControlled, selectedUserProp]);

    // Fetch initial options when opening with an empty input.
    useEffect(() => {
        if (open && inputValue.trim() === "" && !hasFetchedEmpty) {
            setHasFetchedEmpty(true);
            latestSearchTermRef.current = "";
            fetchUsersAPI("");
        }
    }, [open, fetchUsersAPI, inputValue, hasFetchedEmpty]);

    /**
     * Handle text input changes and trigger debounced searching.
     */
    const handleInputChange = (event, newInputValue, reason) => {
        const nextValue = newInputValue || "";
        setInputValue(nextValue);
        latestSearchTermRef.current = nextValue;

        // Skip additional work when the change is triggered by selection reset.
        if (reason === "reset") {
            return;
        }

        // Reset the empty fetch guard when the user clears the input.
        if (reason === "clear") {
            setHasFetchedEmpty(false);
        }

        debouncedSearch(nextValue);
    };

    return (
        <Autocomplete
            sx={applyWidth ? { width: 300 } : undefined}
            open={open}
            onOpen={() => {
                setHasFetchedEmpty(false);
                setOpen(true);
            }}
            onClose={() => setOpen(false)}
            disabled={disabled}
            options={options}
            getOptionLabel={(option) => option?.name || ""}
            filterOptions={(x) => x}
            autoComplete
            includeInputInList
            filterSelectedOptions
            loading={loading}
            value={isControlled ? selectedUserProp : selectedUser}
            onChange={(event, newValue) => {
                setSelectedUser(newValue);
                if (newValue) {
                    onUserSelect({
                        id: newValue.id,
                        name: newValue.name,
                        email: newValue.email,
                        isAdmin: newValue.isAdmin,
                    });
                }
            }}
            onInputChange={handleInputChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Search users"
                    variant="outlined"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? (
                                    <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
            renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                    <li key={option.id} {...otherProps}>
                        <div className="option-row">
                            <div>
                                <div className="option-name">{option.name}</div>
                                <div className="option-email">{option.email}</div>
                            </div>
                            {option.alreadyAssigned && (
                                <div className="badge already-assigned">Already added</div>
                            )}
                            {option.isAdmin && (
                                <div className="badge admin">Admin</div>
                            )}
                        </div>
                    </li>
                );
            }}
            noOptionsText="No users found"
        />
    );
};

export default UserSelectAutocomplete;
